// Bucket day-level chart data from the raw event arrays. All bucketing uses
// the viewer's local timezone (browser). Stores minute-of-day [0, 1440].

import { parseSegments } from '@shared/feed.ts';
import type {
  DiaperEvent,
  FeedingSession,
  PumpingSession,
  SleepSession,
} from '@shared/types.ts';

export type SleepSeg = {
  start: number; // minute of day
  end: number;
  /** Night/day is purely an aesthetic call based on the block's midpoint —
   *  it has nothing to do with whether the session is still running, so a
   *  live night-time sleep stays "night" colour. */
  kind: 'night' | 'day';
  /** True for the in-progress tail of a session that hasn't been stopped. */
  live: boolean;
  id: string;
  needsReview?: boolean;
};
export type FeedMark = {
  at: number;
  kind: 'bottle' | 'breast' | 'pump' | 'solid';
  id: string;
};
/** Duration-bearing feed bar (currently only breast). One bar per session
 *  spanning earliest segment start → latest segment end, so L/R switches
 *  read as a single feeding rather than two adjacent blocks. Times are
 *  minute-of-day [0, 1440]; cross-midnight sessions are split per day. */
export type FeedBar = {
  start: number;
  end: number;
  kind: 'breast';
  id: string;
};
export type DiaperMark = { at: number; kind: 'pee' | 'poop' | 'both'; id: string };

export type ChartDay = {
  date: Date; // local midnight
  label: string;
  sleeps: SleepSeg[];
  feedBars: FeedBar[];
  feeds: FeedMark[];
  diapers: DiaperMark[];
};

const DAY = 86400;

export function bucketChartDays(opts: {
  days: number;
  now?: Date;
  sleep: SleepSession[];
  feed: FeedingSession[];
  pump: PumpingSession[];
  diaper: DiaperEvent[];
}): ChartDay[] {
  const now = opts.now ?? new Date();
  const days: ChartDay[] = [];
  for (let i = 0; i < opts.days; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    days.push({
      date: d,
      label: labelFor(d, i),
      sleeps: [],
      feedBars: [],
      feeds: [],
      diapers: [],
    });
  }
  // index by midnight epoch sec
  const dayBySec = new Map<number, ChartDay>();
  for (const dd of days) dayBySec.set(Math.floor(dd.date.getTime() / 1000), dd);

  // sleep — may cross midnights
  for (const s of opts.sleep) {
    const startSec = s.started_at;
    const endSec = s.ended_at ?? Math.floor(now.getTime() / 1000);
    let curSec = startSec;
    while (curSec < endSec) {
      const localStart = new Date(curSec * 1000);
      const localMidnight = new Date(localStart);
      localMidnight.setHours(0, 0, 0, 0);
      const dayKey = Math.floor(localMidnight.getTime() / 1000);
      const day = dayBySec.get(dayKey);
      const nextMidnight = dayKey + DAY;
      const segEnd = Math.min(endSec, nextMidnight);
      if (day) {
        const startMin = Math.max(0, Math.floor((curSec - dayKey) / 60));
        const endMin = Math.min(1440, Math.ceil((segEnd - dayKey) / 60));
        if (endMin > startMin) {
          const kind: SleepSeg['kind'] = isNightSpan(startMin, endMin) ? 'night' : 'day';
          const live = s.ended_at == null && segEnd === endSec;
          day.sleeps.push({
            start: startMin,
            end: endMin,
            kind,
            live,
            id: s.id,
            needsReview: !!s.needs_review,
          });
        }
      }
      curSec = segEnd;
    }
  }

  for (const f of opts.feed) {
    if (f.type === 'breast_left' || f.type === 'breast_right') {
      // One bar per session spanning the full duration. Segments encode
      // L/R switches but for chart purposes we just want total feeding
      // time. Fall back to started_at → ended_at (or now) when segments
      // are missing.
      const segs = parseSegments(f.segments);
      let startSec: number;
      let endSec: number;
      if (segs.length > 0) {
        startSec = segs[0].from;
        endSec = segs[segs.length - 1].to;
      } else {
        startSec = f.started_at;
        endSec = f.ended_at ?? Math.floor(now.getTime() / 1000);
      }
      addBreastBar(dayBySec, startSec, endSec, f.id);
      continue;
    }
    addMark(dayBySec, f.started_at, {
      at: 0,
      kind: f.type === 'bottle' ? 'bottle' : 'solid',
      id: f.id,
    }, 'feeds');
  }

  for (const p of opts.pump) {
    addMark(dayBySec, p.started_at, { at: 0, kind: 'pump', id: p.id }, 'feeds');
  }

  for (const d of opts.diaper) {
    addMark(dayBySec, d.occurred_at, { at: 0, kind: d.kind, id: d.id }, 'diapers');
  }

  return days;
}

function addBreastBar(
  dayBySec: Map<number, ChartDay>,
  startSec: number,
  endSec: number,
  id: string,
) {
  if (endSec <= startSec) return;
  let curSec = startSec;
  while (curSec < endSec) {
    const localStart = new Date(curSec * 1000);
    const localMidnight = new Date(localStart);
    localMidnight.setHours(0, 0, 0, 0);
    const dayKey = Math.floor(localMidnight.getTime() / 1000);
    const day = dayBySec.get(dayKey);
    const nextMidnight = dayKey + DAY;
    const segEnd = Math.min(endSec, nextMidnight);
    if (day) {
      const startMin = Math.max(0, Math.floor((curSec - dayKey) / 60));
      const endMin = Math.min(1440, Math.ceil((segEnd - dayKey) / 60));
      if (endMin > startMin) {
        day.feedBars.push({ start: startMin, end: endMin, kind: 'breast', id });
      }
    }
    curSec = segEnd;
  }
}

function addMark<K extends 'feeds' | 'diapers'>(
  dayBySec: Map<number, ChartDay>,
  sec: number,
  mark: FeedMark | DiaperMark,
  bucket: K,
) {
  const local = new Date(sec * 1000);
  const midnight = new Date(local);
  midnight.setHours(0, 0, 0, 0);
  const dayKey = Math.floor(midnight.getTime() / 1000);
  const day = dayBySec.get(dayKey);
  if (!day) return;
  const at = Math.max(0, Math.min(1440, Math.floor((sec - dayKey) / 60)));
  const m = { ...mark, at };
  if (bucket === 'feeds') day.feeds.push(m as FeedMark);
  else day.diapers.push(m as DiaperMark);
}


function isNightSpan(startMin: number, endMin: number): boolean {
  // Heuristic: any block whose midpoint falls in 19:00–07:00 is night
  const mid = (startMin + endMin) / 2;
  return mid >= 19 * 60 || mid <= 7 * 60;
}

function labelFor(d: Date, _daysAgo: number): string {
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${dow} ${d.getDate()}`;
}
